import { useState } from 'react';

import { Alert } from '@zooplus/ui-components';

import BaseModal from '@components/Overlays/Modals/BaseModal/BaseModal';
import styles from '@components/Overlays/Modals/BaseModal/BaseModal.module.scss';
import { useTranslation } from '@hooks/useTranslations';
import { useCreditcardService } from '@providers/CreditCardsMutationProvider';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';
import { captureException } from '@services/sentryService';

/**
 * Modal to make the customer confirms that he really wants to delete all his stored credit cards.
 * Independent from the actual payment service provider.
 */
const DeleteAllCardsModal = () => {
  const { t } = useTranslation();
  const { deleteAllCards } = useCreditcardService();
  const { hideOverlay } = useOverlays();

  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [deleteAllError, setDeleteAllError] = useState<string>();

  const handleDeleteAllConfirm = () => {
    setDeleteAllLoading(true);
    setDeleteAllError(undefined);

    deleteAllCards()
      .then(() => {
        // zpp-api does not return a boolean on the deleteAllCards endpoint.
        // When the response is deserialized in shop-checkout, it defaults to false and returns it to payment-frontend.
        // So we are always displaying an error, even though all cards have been deleted.
        // This is disabled until the situation is fixed.
        // if (isSuccess) {
        hideOverlay(OverlayIds.DELETE_ALL_CARDS);
        // } else {
        //   setDeleteAllError(t('checkout.payment.creditcard.error.deleteallcards.apicall'));
        // }
        setDeleteAllLoading(false);
      })
      .catch((error: unknown) => {
        // on any unexpected error, send it to Sentry and display a generic error message to the user
        captureException({ error });
        setDeleteAllError(t('checkout.payment.creditcard.error.deleteallcards.apicall'));
        setDeleteAllLoading(false);
      });
  };

  return (
    <BaseModal
      modalId={OverlayIds.DELETE_ALL_CARDS}
      disabledPrimaryBtn={deleteAllLoading}
      disabledSecondaryBtn={deleteAllLoading}
      isLoading={deleteAllLoading}
      handleSubmit={handleDeleteAllConfirm}
      heading={t('checkout.payment.creditcard.delete')}
      primaryButtonLabel={t('general.button.delete')}
      headingId="delete-all-cards"
      size="sm"
      closeBtnAriaLabel={t('checkout.payment.creditcard.delete')}
      dataZta="modal-removeAllCards">
      {deleteAllError && (
        <Alert className={styles.errorMessage} variant="warning">
          {deleteAllError}
        </Alert>
      )}
      <div className={styles.confirmDeleteMessage}>
        {t('checkout.payment.creditcard.deleteAll.confirm')}
      </div>
    </BaseModal>
  );
};

export default DeleteAllCardsModal;
